import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface SelectContextValue {
  value: string;
  onValueChange: (value: string, label: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedLabel: string;
  setSelectedLabel: (label: string) => void;
  itemsRef: React.MutableRefObject<Map<string, string>>;
  /** Labels read from the SelectItem elements in the tree, available before the content ever opens. */
  staticLabels: Map<string, string>;
}

const SelectContext = React.createContext<SelectContextValue | undefined>(undefined);

interface SelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  defaultValue?: string;
}

/**
 * Collect value -> label pairs from the SelectItem elements under `node`.
 * Items only mount while the content is open, so without this pass a closed select
 * showed the raw value ("medium") instead of the item label ("Mediu") until the
 * first click (JUB-190).
 */
function collectItemLabels(node: React.ReactNode, into: Map<string, string>): void {
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) return;
    const props = child.props as { value?: unknown; textValue?: string; children?: React.ReactNode };
    if (child.type === SelectItem && typeof props.value === "string") {
      into.set(props.value, props.textValue ?? (typeof props.children === "string" ? props.children : props.value));
      return;
    }
    if (props.children) collectItemLabels(props.children, into);
  });
}

export const Select = ({ value, onValueChange, children, defaultValue }: SelectProps) => {
  const [internalValue, setInternalValue] = React.useState<string>(defaultValue || "");
  const [open, setOpen] = React.useState(false);
  const [selectedLabel, setSelectedLabel] = React.useState<string>("");
  const itemsRef = React.useRef<Map<string, string>>(new Map());
  const staticLabels = React.useMemo(() => {
    const labels = new Map<string, string>();
    collectItemLabels(children, labels);
    return labels;
  }, [children]);

  const currentValue = value !== undefined ? value : internalValue;
  const handleValueChange = (newValue: string, label: string) => {
    if (value === undefined) {
      setInternalValue(newValue);
    }
    setSelectedLabel(label);
    onValueChange?.(newValue);
    setOpen(false);
  };

  // Update selected label when value changes
  React.useEffect(() => {
    if (currentValue && itemsRef.current.has(currentValue)) {
      setSelectedLabel(itemsRef.current.get(currentValue) || "");
    }
  }, [currentValue]);

  return (
    <SelectContext.Provider value={{ 
      value: currentValue, 
      onValueChange: handleValueChange, 
      open, 
      setOpen, 
      selectedLabel, 
      setSelectedLabel,
      itemsRef,
      staticLabels,
    }}>
      <div className="relative">{children}</div>
    </SelectContext.Provider>
  );
};

interface SelectTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
}

export const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(
  ({ className, children, ...props }, ref) => {
    const context = React.useContext(SelectContext);
    if (!context) {
      throw new Error("SelectTrigger must be used within Select");
    }

    return (
      <button
        ref={ref}
        type="button"
        role="combobox"
        aria-expanded={context.open}
        onClick={() => context.setOpen(!context.open)}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        {...props}
      >
        {children}
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>
    );
  }
);
SelectTrigger.displayName = "SelectTrigger";

interface SelectValueProps {
  placeholder?: string;
  children?: React.ReactNode;
}

export const SelectValue = ({ placeholder, children }: SelectValueProps) => {
  const context = React.useContext(SelectContext);
  if (!context) {
    throw new Error("SelectValue must be used within Select");
  }

  if (children) {
    return <>{children}</>;
  }

  // Mounted items win (they carry textValue for complex children), then the labels read from
  // the element tree, then the label of the last pick, then the raw value.
  const displayValue =
    (context.value && (context.itemsRef.current.get(context.value) || context.staticLabels.get(context.value))) ||
    context.selectedLabel ||
    context.value ||
    placeholder;
  return <span className={cn(!context.value && "text-muted-foreground")}>{displayValue}</span>;
};

interface SelectContentProps {
  children: React.ReactNode;
  className?: string;
}

export const SelectContent = ({ children, className }: SelectContentProps) => {
  const context = React.useContext(SelectContext);
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contentRef.current && !contentRef.current.contains(event.target as Node)) {
        context?.setOpen(false);
      }
    };

    if (context?.open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [context?.open]);

  if (!context) {
    throw new Error("SelectContent must be used within Select");
  }

  if (!context.open) {
    return null;
  }

  return (
    <div
      ref={contentRef}
      className={cn(
        "absolute z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md top-full mt-1 w-full",
        className
      )}
    >
      <div className="p-1">{children}</div>
    </div>
  );
};

interface SelectItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  children: React.ReactNode;
  /** Text value to display when selected, use when children is a complex element */
  textValue?: string;
}

export const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  ({ className, value, children, textValue, ...props }, ref) => {
    const context = React.useContext(SelectContext);
    if (!context) {
      throw new Error("SelectItem must be used within Select");
    }

    const isSelected = context.value === value;
    // Use textValue if provided, otherwise try to extract text from children
    const label = textValue ?? (typeof children === 'string' ? children : value);

    // Register this item's label
    React.useEffect(() => {
      context.itemsRef.current.set(value, label);
      // If this is the selected item, update the label
      if (isSelected) {
        context.setSelectedLabel(label);
      }
      return () => {
        context.itemsRef.current.delete(value);
      };
    }, [value, label, isSelected, context]);

    return (
      <div
        ref={ref}
        role="option"
        aria-selected={isSelected}
        onClick={() => {
          context.onValueChange(value, label);
        }}
        className={cn(
          "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 px-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          isSelected && "bg-accent text-accent-foreground",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
SelectItem.displayName = "SelectItem";
